/**
 * The web's mirror of the server's capture cadence policy (NetCoreInterfaces.CaptureCadence).
 *
 * Golf and every non-movement sport split at the fixed 10 fps, so the upload page can extract pose
 * on-device and pair it with the split frames BY INDEX. Movement sports split at the clip's NATIVE
 * rate, which only the splitter knows (it probes the file), so their pose is extracted AFTER the
 * split from the split frames themselves — frame-keyed, cadence-agnostic — exactly as an upload
 * with no pose file is labelled today. The server stamps that pose file with the upload's stored fps.
 *
 * captureCadence.test.ts trips on drift with the sport table (sportIdsForFamily("Movement")).
 */
import { sportIdsForFamily } from "../api/adapters";

/** The Movement GROUP is a display family; the server's native-rate list is not. The vertical jump's
 * FRONT view (48) sits in the Movement group beside the side view but is NOT in the server's
 * CaptureCadence.IsMovementSport (runGait 22, verticalJump 23, squatLift 24): a single-event technique
 * stays at the fixed 10 fps. Subtracting it here keeps this mirror equal to the server rather than to
 * the group. */
const FIXED_CADENCE_DESPITE_MOVEMENT_GROUP: ReadonlySet<number> = new Set([48]);

/** The sports the splitter captures at the source's native rate: runGait, verticalJump, squatLift.
 *
 * Built on FIRST USE, not at module load: the set is derived from the sport table, so evaluating
 * it at import time made every module that (indirectly) imports this one require a complete
 * `../api/adapters` — five SessionDetail suites that partially mock adapters died with
 * "sportIdsForFamily is not a function" the moment the session page imported this rule. Lazy
 * keeps one source of truth for the cadence without dragging the sport table into every importer.
 */
let nativeRateIds: ReadonlySet<number> | null = null;
function nativeRateSportIds(): ReadonlySet<number> {
  if (nativeRateIds == null) {
    nativeRateIds = new Set(
      sportIdsForFamily("Movement").filter((id) => !FIXED_CADENCE_DESPITE_MOVEMENT_GROUP.has(id)),
    );
  }
  return nativeRateIds;
}

/** Reads like the old constant (`.has(id)`, iterable) but resolves the table on first touch. */
export const NATIVE_RATE_SPORT_IDS: ReadonlySet<number> = {
  has: (id: number) => nativeRateSportIds().has(id),
  get size() {
    return nativeRateSportIds().size;
  },
  keys: () => nativeRateSportIds().keys(),
  values: () => nativeRateSportIds().values(),
  entries: () => nativeRateSportIds().entries(),
  forEach: (cb: (v: number, v2: number, s: ReadonlySet<number>) => void, thisArg?: unknown) =>
    nativeRateSportIds().forEach(cb, thisArg),
  [Symbol.iterator]: () => nativeRateSportIds()[Symbol.iterator](),
} as ReadonlySet<number>;

export type UploadExtractionDecision = "extract" | "afterSplit" | "awaitSport";

/** What the upload page does about pose for the chosen sport: extract now at the fixed cadence,
 * leave it to the after-split labelling, or wait until a sport is chosen (the sport decides). */
export function uploadExtractionDecision(sportId: number | null | undefined): UploadExtractionDecision {
  if (sportId == null) return "awaitSport";
  return NATIVE_RATE_SPORT_IDS.has(sportId) ? "afterSplit" : "extract";
}
